import {createTheme} from "@mui/material/styles";
import {palette, semanticColors} from "../theme/palette.ts";
import {components} from "../theme/components";
import * as locales from "@mui/material/locale";

export type SupportedLocales = keyof typeof locales;
export type ThemeMode = "light" | "dark";

class MuiThemeLocaleAdapter {
    locale: SupportedLocales = "deDE";
    mode: ThemeMode = "light";
    theme = this.buildTheme();

    buildTheme (mode: ThemeMode = this.mode) {
        return createTheme({
            typography: (() => {
                const lato = "'Lato', system-ui, Arial, sans-serif";
                return {
                    fontFamily: [
                        "\"Lato\"",
                        "\"dinp-500\"",
                        "\"dinp-700\"",
                        "\"sofo-400\"",
                        "\"sofo-600\"",
                        "system-ui",
                        "Arial",
                        "sans-serif",
                        "icongo",
                    ].join(","),
                    // Colors reference CSS vars so they flip with the
                    // [data-theme="dark"] selector on <html> without rebuilding
                    // the MUI theme tree. Brand-tinted heading uses
                    // --an-heading (dark green in light, mint in dark).
                    h1: {fontFamily: lato, fontWeight: 700, fontSize: "36px", lineHeight: "44px", color: "var(--an-heading)", letterSpacing: "-0.01em"},
                    h2: {fontFamily: lato, fontWeight: 700, fontSize: "24px", lineHeight: "32px", color: "var(--an-heading)"},
                    h3: {fontFamily: lato, fontWeight: 700, fontSize: "20px", lineHeight: "28px", color: "var(--an-heading)"},
                    h4: {fontFamily: lato, fontWeight: 700, fontSize: "18px", lineHeight: "28px", color: "var(--an-ink)"},
                    h5: {fontFamily: lato, fontWeight: 700, fontSize: "16px", lineHeight: "24px", color: "var(--an-ink)"},
                    h6: {fontFamily: lato, fontWeight: 700, fontSize: "14px", lineHeight: "20px", color: "var(--an-ink)"},
                    subtitle1: {fontFamily: lato, fontWeight: 700, fontSize: "16px", lineHeight: "24px", color: "var(--an-ink)"},
                    subtitle2: {fontFamily: lato, fontWeight: 400, fontSize: "14px", lineHeight: "20px", color: "var(--an-muted-3)"},
                    body1: {fontFamily: lato, fontWeight: 400, fontSize: "16px", lineHeight: "24px", color: "var(--an-ink)"},
                    body2: {fontFamily: lato, fontWeight: 400, fontSize: "14px", lineHeight: "20px", color: "var(--an-muted-2)"},
                    caption: {fontFamily: lato, fontWeight: 400, fontSize: "12px", lineHeight: "18px", color: "var(--an-muted-3)"},
                    button: {fontFamily: lato, fontWeight: 700, fontSize: "16px", lineHeight: 1, textTransform: "none", letterSpacing: 0},
                    overline: {fontFamily: lato, fontWeight: 700, fontSize: "12px", lineHeight: "18px", color: "var(--an-muted-3)", letterSpacing: "0.06em", textTransform: "uppercase"},
                };
            })(),
            shape: {
                borderRadius: 4,
            },
            // Switches MUI primitives (TextField outline, Button, Select,
            // Menu, Dialog, Tooltip etc.) between built-in light and dark
            // defaults. Custom an-* tokens flip independently via CSS vars
            // on [data-theme="dark"]. We also pin background.paper/default
            // to brand surfaces so MUI components that read those (e.g.
            // mui-tiptap toolbar) don't fall back to MUI's grey `#121212`.
            palette: {
                mode,
                ...palette,
                // #55070: error/success/warning/info per mode so form
                // validation, alerts & status colors stay on-brand and
                // readable on the dark surfaces (MUI's dark default
                // #f44336 failed AA on them).
                ...semanticColors(mode),
                background: mode === "dark"
                    ? {default: "#0B1120", paper: "#1F2937"}
                    : {default: "#E9EAEB", paper: "#FFFFFF"},
            },
            components,
        // Passed as its own argument so createTheme deep-merges it. Spread into
        // `components`, the locale's entry for a component (deDE brings MuiAlert and
        // MuiAutocomplete) replaced our override of it - only in German, since enUS
        // brings none.
        }, locales[this.locale]);
    }

    setMode (mode: ThemeMode) {
        if (this.mode === mode) return;
        this.mode = mode;
        this.theme = this.buildTheme();
    }

    mapLocale (locale: string): SupportedLocales {
        switch (locale) {
            case "de":
                return "deDE";
            case "en":
                return "enUS";
            default:
                return "enUS";
        }
    };

    setLocale (locale: string) {
        locale = this.mapLocale(locale);
        if (this.locale === locale) return;
        this.locale = locale as SupportedLocales;
        this.theme = this.buildTheme();
    }
}

export const muiThemeLocaleAdapter = new MuiThemeLocaleAdapter();
